import * as React from 'react';
import propz from 'propz';
import BPromise from 'bluebird';
import { parse } from 'query-string';
import { AppUser } from 'Src/views/App/App';
import { Component } from 'react';
import { History, Location } from 'history';
import { Loader } from 'Src/components/Loader/Loader';
import { EventInvitationMessage } from 'Src/models/message';
import { DATE_INTERVAL, FILTER_TYPE, FIRST_PAGE, CARD_LIMIT } from 'Src/consts/table';
import {
  ColumnDefinition,
  getFilters2,
  getServerQueryFilter2,
  isSortExist,
  TABLE_SORT_DIRECTION,
  getSearchFilter,
  getSearchOrder
} from 'Src/helpers/table/table';
import { getParentArchiveMessages, getParentArchiveMessagesCount } from 'Src/helpers/service/parent/messagesTable';
import { getChildren } from 'Src/helpers/service/parent/children';
import { Pagination2 } from 'Src/components/Pagination/Pagination2';
import { ParentFilterBar } from 'Src/components/ParentFilterBar/ParentFilterBar';
import { AppliedTextFilterChip } from 'Src/components/AppliedTextFilterChip/AppliedTextFilterChip';
import { ItemsBar } from 'Src/views/GenericView/ParentView/Calendar/ItemsBar';
import { AvailabilityReportCard } from 'Src/components/AvailabilityReportCard/AvailabilityReportCard';
import { ParentAvailabilityOverview } from 'Src/components/ParentAvailabilityOverview/ParentAvailabilityOverview';
import { INVITE_TYPE } from 'Src/consts/invite';
import { Map } from 'Src/components/Map/Map';
import { SimpleModal } from 'Src/components/SimpleModal/SimpleModal';
import { AvailabilityReportsDetails } from '../AvailabilityReportsDetails/AvailabilityReportsDetails';
import { SchoolEvent } from 'Src/models/event';
import { ParentPageHeader } from '../../components/ParentPageHeader';
import '../../components/ParentLists.scss';
import { currentAcademicYearRange } from 'Src/helpers/club/clubSectionCounts';

interface Props {
  user: AppUser;
  history: History;
  location: Location;
  mode?: 'current' | 'previous';
}

const availabilityArchiveRange = (mode?: 'current' | 'previous') => {
  const current = currentAcademicYearRange();
  if (mode !== 'previous') return current;
  const end = new Date(`${current.from}T00:00:00Z`);
  end.setUTCDate(end.getUTCDate() - 1);
  return { to: end.toISOString().slice(0, 10) };
};

interface State {
  items: EventInvitationMessage[];
  selectedItem: EventInvitationMessage | undefined;
  event: SchoolEvent | undefined;
  total: number;
  currentPage: number;
  filters: any;
  children: any[];
  sortFieldName: string;
  sortDirection: string;
  isFiltersOpen: boolean;
  isLoading: boolean;
  basePath: string;
  isViewMapModalOpen: boolean;
  isShowDetailsModalOpen: boolean;
  childEventsById: Record<string, SchoolEvent>;
}

const FILTER_FIELDS_ALL: ColumnDefinition[] = [
  { text: 'Children', field: 'childIdList', type: FILTER_TYPE.MULTISELECT, accessor: [''], isSort: false },
  { text: 'Sport', field: 'sport', type: FILTER_TYPE.TEXT, accessor: [''], isSort: false },
  { text: 'Event date', field: 'startTime', type: FILTER_TYPE.DATE_INTERVAL, accessor: [''], isSort: true },
  { text: 'Participation', field: 'isTakePart', type: FILTER_TYPE.SELECT, accessor: [''], isSort: false }
];

const toIso = (s?: string) => {
  if (!s) return s;
  const m1 = s.match(/^(\d{2})[\/-](\d{2})[\/-](\d{4})$/);
  const m2 = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m1) return `${m1[3]}-${m1[2]}-${m1[1]}`;
  if (m2) return s;
  return s;
};

export class AvailabilityReportsArchive extends Component<Props, State> {
  private loadRequestId = 0;

  constructor(props) {
    super(props);

    this.state = {
      items: [],
      selectedItem: undefined,
      event: undefined,
      total: 0,
      currentPage: FIRST_PAGE,
      filters: {},
      sortFieldName: '',
      sortDirection: '',
      isFiltersOpen: false,
      isLoading: false,
      basePath: '',
      children: [],
      isViewMapModalOpen: false,
      isShowDetailsModalOpen: false,
      childEventsById: {}
    };
  }

  componentDidMount() {
    this.setState({ isLoading: true });
    this.setItems();
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.location.search !== this.props.location.search || nextProps.mode !== this.props.mode) {
      this.setState({ isLoading: true });
      this.setItems(nextProps);
    }
  }

  private normalizeFiltersForQuery = (f: any) => {
    const src = { ...(f || {}) };
    const out: any = {};

    if (typeof src.sport === 'string') {
      const s = src.sport.trim();
      if (s) out.sport = s;
    }

    if (typeof src.startTime === 'string') {
      out.startTime = { from: toIso(src.startTime), to: undefined };
    } else if (src.startTime && (src.startTime.from || src.startTime.to)) {
      out.startTime = { from: toIso(src.startTime.from), to: toIso(src.startTime.to) };
    }

    if (src.isTakePart === 'yes') out.isTakePart = true;
    if (src.isTakePart === 'no') out.isTakePart = false;

    if (Array.isArray(src.childIdList) && src.childIdList.length) {
      out.childIdList = src.childIdList;
    }

    out.kind = 'EventParticipationMessage';
    return out;
  };

  private setItems(props: Props = this.props) {
    const requestId = ++this.loadRequestId;
    const { user, location } = props;
    const search = parse(location.search);

    const page = typeof search.page !== 'undefined' ? Number(search.page) : FIRST_PAGE;

    let sortDirection = '' as TABLE_SORT_DIRECTION;
    let sortFieldName = '';

    if (typeof search.order === 'string') {
      const [field, dir] = (search.order as string).split(':');
      sortFieldName = field || '';
      sortDirection = (dir as TABLE_SORT_DIRECTION) || ('' as TABLE_SORT_DIRECTION);
    }

    const sortByFieldExist = !!sortFieldName;
    const sortDirectionExist = !!sortDirection;

    const raw = getFilters2(FILTER_FIELDS_ALL, search);

    const blob = propz.get(search, ['filter']);
    let isTakePart: 'all' | 'yes' | 'no' | undefined = undefined;
    if (blob) {
      try {
        const parsed = JSON.parse(decodeURIComponent(String(blob)));
        const v = parsed?.where?.isTakePart;
        if (typeof v === 'boolean') isTakePart = v ? 'yes' : 'no';
      } catch {}
    }

    const takeParam = (search.take as string) || '';
    if (takeParam === 'yes' || takeParam === 'no' || takeParam === 'all') {
      isTakePart = takeParam;
    }

    const uiFiltersBase = {
      ...raw,
      ...(typeof isTakePart !== 'undefined' ? { isTakePart } : {})
    };
    const hasAppliedArchiveFilters = Object.keys(search).some(key => key !== 'page' && key !== 'order');
    if (!uiFiltersBase.startTime && props.mode !== 'previous') {
      uiFiltersBase.startTime = availabilityArchiveRange(props.mode);
    }

    if (props.mode === 'previous' && !hasAppliedArchiveFilters) {
      return getChildren(user).then(children => {
        if (requestId !== this.loadRequestId) return;
        this.setState({
          items: [],
          currentPage: FIRST_PAGE,
          children,
          filters: uiFiltersBase,
          sortDirection,
          sortFieldName,
          total: 0,
          basePath: location.pathname,
          isLoading: false,
          childEventsById: {}
        });
      });
    }

    const filtersForQuery = this.normalizeFiltersForQuery(uiFiltersBase);

    const order = sortByFieldExist && sortDirectionExist ? `${sortFieldName} ${sortDirection}` : undefined;

    const serverQueryFilter = getServerQueryFilter2(page, filtersForQuery, order, CARD_LIMIT);

    const countWhere = { ...filtersForQuery };
    delete countWhere.childIdList;

    const getItemsPromise = getParentArchiveMessages(user, serverQueryFilter);
    const getItemsCountProm = getParentArchiveMessagesCount(user, countWhere, true);
    const getChildrenProm = getChildren(user);

    let children: any[] = [];

    return BPromise.all([getItemsPromise, getItemsCountProm, getChildrenProm]).then(([items, countObj, _children]) => {
      if (requestId !== this.loadRequestId) return;
      const hasChildSelection = Array.isArray(uiFiltersBase.childIdList) && uiFiltersBase.childIdList.length > 0;
      const itemCounts = countObj.itemCounts || {};
      const matchingTotal = hasChildSelection && countObj.itemCounts
        ? uiFiltersBase.childIdList.reduce((sum, childId) => sum + (itemCounts[String(childId)] || 0), 0)
        : Number(countObj.count);
      children = (_children || []).map(child => ({ ...child, itemCount: itemCounts[String(child.id)] || 0 }));

      const uiFiltersWithChild = hasChildSelection
        ? uiFiltersBase
        : { ...uiFiltersBase, childIdList: children.map(c => c.id) };

      const childEventsById: Record<string, SchoolEvent> = {};
      items.forEach(item => {
        const eventData = propz.get(item, ['eventData']) as SchoolEvent;
        if (eventData?.id) childEventsById[String(eventData.id)] = eventData;
      });

      this.setState({
        items,
        currentPage: page,
        children,
        filters: uiFiltersWithChild,
        sortDirection,
        sortFieldName,
        total: matchingTotal,
        basePath: location.pathname,
        isLoading: false,
        childEventsById
      });
    });
  }

  private pushUrlFromState = () => {
    const { sortDirection, sortFieldName, basePath, filters } = this.state;
    const parts: string[] = [];

    if (isSortExist(sortDirection, sortFieldName)) {
      parts.push(getSearchOrder(sortDirection, sortFieldName));
    }

    const filtersForUrl = this.normalizeFiltersForQuery(filters);
    const appliedFilters = { ...filtersForUrl };
    delete appliedFilters.kind;
    if (Object.keys(appliedFilters).length > 0) {
      parts.push(getSearchFilter(filtersForUrl));
    }

    const take = filters.isTakePart as 'all' | 'yes' | 'no' | undefined;
    if (take && take !== 'all') parts.push(`take=${take}`);
    else if (take === 'all') parts.push('take=all');

    this.props.history.push({
      pathname: basePath || this.props.history.location.pathname,
      search: parts.join('&')
    });
  };

  private setCurrentPageParams = (currentPage: number): void => {
    const { filters, sortFieldName, sortDirection, basePath } = this.state;
    const parts: string[] = [];

    if (currentPage !== 1) parts.push(`page=${currentPage}`);
    if (isSortExist(sortDirection, sortFieldName)) {
      parts.push(getSearchOrder(sortDirection, sortFieldName));
    }

    const filtersForUrl = this.normalizeFiltersForQuery(filters);
    const appliedFilters = { ...filtersForUrl };
    delete appliedFilters.kind;
    if (Object.keys(appliedFilters).length > 0) {
      parts.push(getSearchFilter(filtersForUrl));
    }

    const take = filters.isTakePart as 'all' | 'yes' | 'no' | undefined;
    if (take && take !== 'all') parts.push(`take=${take}`);
    else if (take === 'all') parts.push('take=all');

    this.props.history.push({ pathname: basePath, search: parts.join('&') });
  };

  private onTableFilterChange = (event, filterField: string, options?): void => {
    const filterValue = propz.get(event, ['target', 'value'], '');
    const { filters } = this.state;
    const currentFilterField = filters[filterField];

    let nextFilters = { ...filters };

    if (typeof options !== 'undefined') {
      switch (options) {
        case DATE_INTERVAL.FROM:
          nextFilters = { ...nextFilters, [filterField]: { ...currentFilterField, from: filterValue } };
          break;
        case DATE_INTERVAL.TO:
          nextFilters = { ...nextFilters, [filterField]: { ...currentFilterField, to: filterValue } };
          break;
      }
    } else {
      const def = FILTER_FIELDS_ALL.find(col => col.field === filterField);
      const filterType = def?.type;

      if (filterType === FILTER_TYPE.MULTISELECT) {
        const optionsEl = event.target.options;
        const value: string[] = [];
        for (let i = 0; i < optionsEl.length; i++) {
          if (optionsEl[i].selected) value.push(optionsEl[i].value);
        }
        nextFilters = { ...nextFilters, [filterField]: value };
      } else {
        nextFilters = { ...nextFilters, [filterField]: filterValue };
      }
    }

    this.setState({ filters: nextFilters });
  };

  private onApplyFilterClick = (): void => {
    this.pushUrlFromState();
  };

  private onClearFilterClick = (): void => {
    this.props.history.push({ pathname: this.state.basePath, search: '' });
  };

  private onChildPickerClick = (childId: string): void => {
    const { children } = this.state;

    const nextFilters = {
      ...this.state.filters,
      childIdList: childId === 'all' ? children.map(c => c.id) : [childId]
    };

    this.setState({ filters: nextFilters }, this.pushUrlFromState);
  };

  private renderViewMapModal = () => {
    const { isViewMapModalOpen, event } = this.state;

    const point =
      propz.get(event, ['venue', 'postcodeData', 'point']) ||
      propz.get(event, ['venue', 'placeData', 'point']) ||
      propz.get(event, ['venue', 'point']) ||
      undefined;

    return (
      <SimpleModal isOpen={isViewMapModalOpen} onCloseClick={this.closeViewMapModal} title="View map">
        <Map point={point} customStylingClass="eChildEventVenueMap" isMarkerDraggable={false} />
      </SimpleModal>
    );
  };

  private openViewMapModal = (item: EventInvitationMessage) => {
    const eventId = String(propz.get(item, ['eventData', 'id'], ''));
    const ev = this.state.childEventsById[eventId];
    this.setState({ isViewMapModalOpen: true, selectedItem: item, event: ev });
  };
  private closeViewMapModal = () =>
    this.setState({ isViewMapModalOpen: false, selectedItem: undefined, event: undefined });

  private renderShowDetailsModal = () => {
    const { user } = this.props;
    const { activeSchoolId } = user;
    const { isShowDetailsModalOpen, selectedItem, event } = this.state;

    return (
      <SimpleModal
        isOpen={isShowDetailsModalOpen}
        onCloseClick={this.closeShowDetailsModal}
        title="Availability report details"
        side="right"
        buttonCancelText="Close"
      >
        <AvailabilityReportsDetails item={selectedItem} event={event} activeSchoolId={activeSchoolId} />
      </SimpleModal>
    );
  };

  private openShowDetailsModal = (item: EventInvitationMessage) => {
    const eventId = String(propz.get(item, ['eventData', 'id'], ''));
    this.setState({ isShowDetailsModalOpen: true, selectedItem: item, event: this.state.childEventsById[eventId] });
  };

  private closeShowDetailsModal = () => {
    this.setState({ isShowDetailsModalOpen: false, selectedItem: undefined, event: undefined });
  };

  render() {
    const { isLoading, isFiltersOpen, total, currentPage, items, children, filters } = this.state;
    const archiveSearch = parse(this.props.location.search);
    const hasAppliedArchiveFilters = Object.keys(archiveSearch).some(key => key !== 'page' && key !== 'order');

    if (isLoading) return <Loader />;

    const selectedChildIds =
      filters.childIdList && filters.childIdList.length ? filters.childIdList : children.map(c => c.id);

    return (
      <main className="parentPage parentListPage parentAvailabilityPage">
        {this.renderViewMapModal()}
        {this.renderShowDetailsModal()}

        <ParentPageHeader
          title="Availability"
          tabs={[
            { label: 'Upcoming', onClick: () => this.props.history.push('/availabilityReports/outbox') },
            {
              label: 'Past this year',
              active: this.props.mode !== 'previous',
              onClick: () => this.props.history.push('/availabilityReports/history')
            },
            {
              label: 'Previous years',
              active: this.props.mode === 'previous',
              onClick: () => this.props.history.push('/availabilityReports/archive')
            }
          ]}
        />

        <ItemsBar
          items={children}
          itemIdList={selectedChildIds}
          onClick={this.onChildPickerClick}
          allItemsTitle="All children"
          getItemName={(c: any) => `${c.firstName} ${c.lastName}`}
        />

        <div className="parentListControls">
          <div className="parentListSearch">
            <input
              type="search"
              className="form-control"
              placeholder="Search activity or sport…"
              value={filters.sport || ''}
              onChange={e => this.onTableFilterChange(e, 'sport')}
              onKeyDown={e => {
                if (e.key === 'Enter') this.onApplyFilterClick();
              }}
            />
          </div>
          <button
            className={isFiltersOpen ? 'parentFilterButton is-active' : 'parentFilterButton'}
            onClick={() => this.setState(prev => ({ isFiltersOpen: !prev.isFiltersOpen }))}
          >
            <span className="parentFilterButton__icon" aria-hidden="true" /> Filters
          </button>

          <Pagination2
            currentPage={currentPage}
            total={total}
            setCurrentPageParams={this.setCurrentPageParams}
            limit={CARD_LIMIT}
            modern
          />
        </div>

        <AppliedTextFilterChip
          history={this.props.history}
          location={this.props.location}
          filters={[
            { filterKey: 'sport', label: 'Activity/Sport' },
            { filterKey: 'startTime', label: 'Event date', type: 'date' },
            {
              filterKey: 'take', label: 'Participation',
              valueLabels: { yes: 'Taking part', no: 'Not taking part', all: 'All' }
            }
          ]}
        />

        {isFiltersOpen && (
          <div className="parentFilterSurface">
            <ParentFilterBar
              filters={filters}
              showTextFilter={false}
              onTableFilterChange={this.onTableFilterChange}
              onApply={this.onApplyFilterClick}
              onClear={this.onClearFilterClick}
              onCancel={() => this.setState({ isFiltersOpen: false })}
              hint={<>To search the archive by activity or sport, also select an event date interval.</>}
              labels={{ text: 'Activity/Sport', date: 'Event date', status: 'Participation' }}
              fieldKeys={{ textKey: 'sport', dateKey: 'startTime', statusKey: 'isTakePart' }}
              showStatus
              statusOptions={[
                { value: 'all', label: 'All' },
                { value: 'yes', label: 'Yes' },
                { value: 'no', label: 'No' }
              ]}
            />
          </div>
        )}

        {items.length === 0 ? (
          <div className="parentListEmpty">
            <strong>
              {this.props.mode === 'previous' && !hasAppliedArchiveFilters
                ? 'Search previous academic years'
                : this.props.mode === 'previous'
                ? 'No availability reports match your filters'
                : 'No availability reports from this academic year'}
            </strong>
            <span>
              {this.props.mode === 'previous' && !hasAppliedArchiveFilters
                ? 'Use the event date and other filters to find an older availability report.'
                : this.props.mode === 'previous'
                ? 'Try changing the event date range or removing one of the other filters.'
                : 'Submitted reports from this academic year will appear here.'}
            </span>
          </div>
        ) : <ParentAvailabilityOverview items={items} user={this.props.user} readOnly onView={this.openShowDetailsModal} />}
      </main>
    );
  }
}
